// src/components/Eventos/UploadLifButton.js

import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Snackbar,
  Alert
} from '@mui/material';
import Papa from 'papaparse';
import { useEventContext } from '../../context/EventContext';
import { parseTime } from '../../utils/ResultUtils'; // para timeRaw

// Helper para parsear splits tipo '57.56 (57.56),2:20.70 (1:23.14),…'
function parseSplits(raw) {
  const clean = raw.replace(/^"|"$/g, '');
  return clean.split(',').map(seg => {
    const m = seg.match(/([^\s(]+)\s*\(\s*([^\)]+)\s*\)/);
    if (m) {
      return { cumulative: m[1], lap: m[2] };
    } else {
      return { cumulative: seg.trim(), lap: seg.trim() };
    }
  });
}

const UploadLifButton = ({ heatDetails }) => {
  const {
    updateParticipantResults,
    calculateRankings,
    getParticipantResults
  } = useEventContext();

  const [openCompare, setOpenCompare] = useState(false);
  const [fileRows,     setFileRows]   = useState([]);
  const [snackbar,     setSnackbar]   = useState({ open:false, message:'', severity:'info' });

  const heatKey = `${heatDetails.eventNumber}_${heatDetails.round}_${heatDetails.heat}`;

  // Mapa UI actual: idNumber → lane string
  const uiMap = {};
  heatDetails.participants.forEach(p => {
uiMap[p.idNumber] = p.lane != null
  ? p.lane.toString()
  : ''; // o el valor que tenga sentido como “sin carril”;
  });

  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      delimiter: ',',
      skipEmptyLines: true,
      complete: ({ data: rows }) => {
        if (rows.length < 2) {
          setSnackbar({ open:true, message:'Archivo vacío o sin filas válidas', severity:'error' });
          return;
        }
        // header = primera fila
        const header = rows[0].map(c => c.trim());
        const fileKey = `${header[0]}_${header[1]}_${header[2]}`;
        if (fileKey !== heatKey) {
          setSnackbar({
            open:true,
            message:`Archivo para ${fileKey}, no coincide con ${heatKey}`,
            severity:'error'
          });
          return;
        }

        const globalWind = (header[4] || '').replace(/\s*\(.*?\)/g, '');
        const parsed = rows.slice(1).reduce((acc, cols) => {
          const place    = cols[0]?.trim().toUpperCase();
          const id       = cols[1]?.trim();
          const lane     = cols[2]?.trim();
          const time     = cols[6]?.trim();
          const delta    = cols[8]?.trim() || undefined;
          const reactionTime = cols[9]?.trim() || undefined;
          const lapRaw    = cols[10]?.trim() || '';
          const delta2       = cols[15]?.trim() || undefined;
          const delta3       = cols[16]?.trim() || undefined;
          const speed        = cols[17]?.trim() || undefined;
          const pace         = cols[18]?.trim() || undefined;

          if (!id) return acc; // descartar filas sin ID

          const lap = lapRaw ? parseSplits(lapRaw) : undefined;

          acc.push({
            id,
            lane,
            place,
            time,
            wind: globalWind,
            deltaTime: delta,
            reactionTime,
            lap,
            deltaTime2: delta2,
            deltaTime3: delta3,
            speed,
            pace
          });
          return acc;
        }, []);

        // detectamos discrepancias carril↔ID
        const mismatches = parsed.filter(r => uiMap[r.id] && uiMap[r.id] !== r.lane);
        setFileRows(parsed);

        if (mismatches.length === 0) {
          // aplicamos todos
          parsed.forEach(r => {
            const existing = getParticipantResults(
              heatDetails.eventNumber,
              heatDetails.round,
              heatDetails.heat,
              r.id
            ) || {};

            // base: special o time+wind
            const base = ['DNS','FS'].includes(r.place)
              ? { special: r.place }
              : {
                  time: r.time,
                  wind: r.wind,
                  timeRaw: parseTime(r.time)
                };

            // extras opcionales
            const extras = {
              ...(r.deltaTime     && { deltaTime: r.deltaTime }),
              ...(r.deltaTime2    && { deltaTime2: r.deltaTime2 }),
              ...(r.deltaTime3    && { deltaTime3: r.deltaTime3 }),
              ...(r.reactionTime  && { reactionTime: r.reactionTime }),
              ...(r.lap        && { lapTimes: r.lap }),
              ...(r.speed         && { speed: r.speed }),
              ...(r.pace          && { pace: r.pace })
            };

            const payload = {
              ...existing,
              ...base,
              ...extras
            };

            updateParticipantResults(
              heatDetails.eventNumber,
              heatDetails.round,
              heatDetails.heat,
              r.id,
              payload
            );
          });

          calculateRankings(
            heatDetails.eventNumber,
            heatDetails.round,
            heatDetails.heat
          );

          setSnackbar({
            open:     true,
            message:  `Importados ${parsed.length} resultados`,
            severity: 'success'
          });
        } else {
          setOpenCompare(true);
          setSnackbar({
            open: true,
            message: `Discrepancias en ${mismatches.length}`,
            severity: 'warning'
          });
        }
      },
      error: err => {
        console.error(err);
        setSnackbar({ open:true, message:'Error parseando archivo', severity:'error' });
      }
    });

    // limpiar input para re-subir mismo archivo si hace falta
    e.target.value = '';
  };

  const applyResults = () => {
    fileRows.forEach(r => {
      const payload = ['DNS','FS'].includes(r.place)
        ? { special: r.place }
        : {
            time: r.time,
            wind: r.wind,
            timeRaw: parseTime(r.time),
            deltaTime: r.deltaTime,
            deltaTime2: r.deltaTime2,
            deltaTime3: r.deltaTime3,
            reactionTime: r.reactionTime,
            lapTimes: r.lap,
            speed: r.speed,
            pace: r.pace
          };

      updateParticipantResults(
        heatDetails.eventNumber,
        heatDetails.round,
        heatDetails.heat,
        r.id,
        payload
      );
    });
    calculateRankings(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat
    );
    setOpenCompare(false);
    setSnackbar({ open:true, message:'Resultados importados', severity:'success' });
  };

  // Filas UI actuales
  const uiRows = heatDetails.participants.map(p => {
    const res = getParticipantResults(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      p.idNumber
    ) || {};
    return {
      id:      p.idNumber,
      lane:    p.lane != null? p.lane.toString(): '',
      time:    res.time || '',
      special: res.special || '',
      wind:    res.wind || ''
    };
  });

  return (
    <>
      <input
        id="upload-lif-input"
        type="file"
        accept=".lif,.csv"
        style={{ display:'none' }}
        onChange={handleFile}
      />
      <label htmlFor="upload-lif-input">
        <Button variant="contained" component="span">
          Subir .lif
        </Button>
      </label>

      {/* Modal de comparación */}
      <Dialog open={openCompare} onClose={() => setOpenCompare(false)} maxWidth="xl" fullWidth>
        <DialogTitle>Validar importación .lif ({heatKey})</DialogTitle>
        <DialogContent>
          <Box sx={{ display:'flex', gap:2, overflowX:'auto' }}>
            {/* Tabla UI actual */}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Carril</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Tiempo</TableCell>
                  <TableCell>Esp.</TableCell>
                  <TableCell>Viento</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uiRows.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.lane}</TableCell>
                    <TableCell>{r.id}</TableCell>
                    <TableCell>{r.time||'-'}</TableCell>
                    <TableCell>{r.special||'-'}</TableCell>
                    <TableCell>{r.wind||'-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Tabla archivo .lif */}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Lugar</TableCell>
                  <TableCell>Carril</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Tiempo</TableCell>
                  <TableCell>Esp.</TableCell>
                  <TableCell>Viento</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fileRows.map(r => {
                  const mismatch = uiMap[r.id] !== r.lane;
                  return (
                    <TableRow key={r.id} sx={mismatch ? { backgroundColor:'#ffe8e8' } : {}}>
                      <TableCell>{r.place||'-'}</TableCell>
                      <TableCell>{r.lane||'-'}</TableCell>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{r.time||'-'}</TableCell>
                      <TableCell>{['DNS','FS'].includes(r.place) ? r.place : '-'}</TableCell>
                      <TableCell>{r.wind||'-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCompare(false)}>Cancelar</Button>
          <Button variant="contained" onClick={applyResults}>Aceptar e importar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open:false }))}
        anchorOrigin={{ vertical:'bottom', horizontal:'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open:false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UploadLifButton;
